/**
 * GoogleWorkspaceConfigPage
 *
 * Admin page to configure the Google Workspace integration for this org.
 * Allows setting the GW domain and disabling the integration.
 *
 * Route: /settings/google-workspace  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { Globe, Trash2, CheckCircle, AlertCircle } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import { motion, AnimatePresence } from "framer-motion";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import Input from "../../components/Inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import Loader from "../../components/Loader/Loader";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import {
  getGoogleWorkspaceConfig,
  setGoogleWorkspaceConfig,
  deleteGoogleWorkspaceConfig,
} from "../../api/adminGoogleWorkspace";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes";

// ─── Page ─────────────────────────────────────────────────────────────────────

const GoogleWorkspaceConfigPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const orgAvailable = UIStore((state) => state.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);

  // Current persisted config (null = not configured)
  const [config, setConfig] = useState(null);
  // Form state
  const [domain, setDomain] = useState("");
  // edit mode = user clicked "Update Domain" on a connected config
  const [editMode, setEditMode] = useState(false);
  // transient probe error — cleared on success or cancel
  const [testError, setTestError] = useState(null);

  // Guard: redirect non-admins
  useEffect(() => {
    if (orgAvailable === false) {
      navigate(ROUTES.SETTINGS.MODELS, { replace: true });
    }
  }, [orgAvailable, navigate]);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGoogleWorkspaceConfig();
      setConfig(data);
      setDomain(data?.domain ?? "");
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load Google Workspace config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleTest = async () => {
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) return;
    setTesting(true);
    setTestError(null);
    try {
      await setGoogleWorkspaceConfig(trimmed);
      await fetchConfig();
      setEditMode(false);
    } catch (err) {
      setTestError(err?.response?.data?.error || "Connection failed. Check the setup steps above and try again.");
    } finally {
      setTesting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setTestError(null);
    setDomain(config?.domain ?? "");
  };

  const handleDisable = async () => {
    setDeleting(true);
    try {
      await deleteGoogleWorkspaceConfig();
      showFeedback("Google Workspace integration disabled.", "success");
      setShowConfirmDisable(false);
      setEditMode(false);
      setTestError(null);
      await fetchConfig();
    } catch {
      showFeedback("Failed to disable integration.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SettingsPageShell title="Google Workspace" description="Connect your Google Workspace directory to automatically sync groups and users, keeping access policies and team membership up to date." maxWidth="3xl">
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* Status banner — hidden while editing to avoid confusion */}
          {!editMode && (
            <AnimatePresence>
              {config ? (
                <motion.div
                  key="configured"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-fill-green-100 dark:bg-fill-green-600/10 border border-edge-green-300 dark:border-edge-green-600/40"
                >
                  <CheckCircle className="w-5 h-5 text-ink-green-600 dark:text-ink-green-300 flex-shrink-0" />
                  <p className="text-sm font-medium text-ink-green-700 dark:text-ink-green-300">
                    Connected — <span className="font-semibold">{config.domain}</span>
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="not-configured"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-surface-muted-slate dark:bg-surface-muted-slate/40 border border-edge-gray-200 dark:border-edge-gray-700"
                >
                  <Globe className="w-5 h-5 text-ink-gray-400 flex-shrink-0" />
                  <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">
                    Not configured. Enter your Google Workspace domain below to enable group sync.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* Config card */}
          <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-dialog-border rounded-xl p-5 flex flex-col gap-4">
            {config && !editMode ? (
              /* Connected — read-only view with actions */
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-ink-ink dark:text-ink-white mb-1">Domain</p>
                  <p className="text-sm font-mono text-ink-gray-700 dark:text-ink-gray-300">{config.domain}</p>
                  {config.updated_at && (
                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                      Last updated {new Date(config.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <SecondaryButton
                    onClick={() => { setDomain(config.domain); setEditMode(true); setTestError(null); }}
                  >
                    Update Domain
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => setShowConfirmDisable(true)}
                    className="flex items-center gap-1.5 text-ink-red-600 hover:text-ink-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Disable
                  </SecondaryButton>
                </div>
              </div>
            ) : (
              /* Not configured or editing — domain input form */
              <>
                <div>
                  <p className="text-sm font-semibold text-ink-ink dark:text-ink-white mb-1">
                    {editMode ? "Update Domain" : "Domain"}
                  </p>
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                    The primary Google Workspace domain for your organisation (e.g.{" "}
                    <code className="font-mono">acme.com</code>). Groups must belong to this domain.
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="gw-domain" className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    Domain *
                  </label>
                  <Input
                    id="gw-domain"
                    placeholder="acme.com"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleTest()}
                  />
                  {testError && (
                    <p className="text-xs text-ink-red-500 dark:text-ink-red-400 flex items-start gap-1 mt-0.5">
                      <AlertCircle size={12} className="flex-shrink-0 mt-[1px]" />
                      {testError}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3 justify-end">
                  {editMode && (
                    <SecondaryButton onClick={handleCancelEdit} disabled={testing}>
                      Cancel
                    </SecondaryButton>
                  )}
                  <PrimaryButton
                    onClick={handleTest}
                    disabled={testing || !domain.trim()}
                  >
                    {testing ? "Connecting…" : editMode ? "Test & Update" : "Connect"}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>

          {/* Prerequisites callout — always visible, describes the one-time Admin Console step */}
          <div className="rounded-xl border border-edge-amber-200 dark:border-edge-amber-700/50 bg-fill-hex-fffbeb dark:bg-fill-amber-700/10 p-4 space-y-3">
            <p className="text-sm font-semibold text-ink-amber-800 dark:text-ink-amber-300">Before you connect — one-time setup required</p>
            <p className="text-xs text-ink-hex-78350f dark:text-ink-amber-200 leading-relaxed">
              A Google Workspace Super Admin must grant Photon read access to your directory. Without this step, saving the domain will appear to succeed but group registration will fail.
            </p>
            <ol className="list-decimal list-outside pl-4 space-y-1.5 marker:text-ink-amber-800 dark:marker:text-ink-amber-300 marker:text-xs marker:font-semibold">
              <li className="text-xs text-ink-hex-78350f dark:text-ink-amber-200 leading-relaxed">
                Ask your GCP project owner for the <strong>Photon backend service account email</strong> — it is listed in your GCP project under <strong>IAM &amp; Admin → Service Accounts</strong> and follows the pattern{" "}
                <code className="font-mono bg-fill-hex-fef3c7 dark:bg-fill-amber-700/30 px-1 py-0.5 rounded">sa-photon-ai@&lt;your-project-id&gt;.iam.gserviceaccount.com</code>.
              </li>
              <li className="text-xs text-ink-hex-78350f dark:text-ink-amber-200 leading-relaxed">
                Open{" "}
                <a href="https://admin.google.com" target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:no-underline">
                  admin.google.com
                </a>{" "}
                → <strong>Account → Admin roles</strong>.
              </li>
              <li className="text-xs text-ink-hex-78350f dark:text-ink-amber-200 leading-relaxed">
                Select the built-in <strong>Groups Reader</strong> role (or create a custom role with Groups read permission).
              </li>
              <li className="text-xs text-ink-hex-78350f dark:text-ink-amber-200 leading-relaxed">
                Click <strong>Assign service accounts</strong>, enter the service account email from step 1, and save.
              </li>
            </ol>
            <div className="pt-1 border-t border-edge-amber-200 dark:border-edge-amber-700/40">
              <a
                href="https://support.google.com/a/answer/1219251"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-ink-amber-800 dark:text-ink-amber-300 underline hover:no-underline"
              >
                Google Workspace Admin roles guide →
              </a>
            </div>
          </div>

          {/* How-it-works note */}
          <div className="rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 bg-surface-muted-card dark:bg-surface-muted-card/60 p-4 space-y-3">
            <p className="text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-300">How Google Workspace group sync works</p>
            <ul className="list-disc list-outside pl-4 space-y-2 marker:text-ink-primary marker:text-sm">
              <li className="text-xs text-ink-gray-500 dark:text-ink-gray-400 leading-relaxed">
                Register specific Google Workspace groups from the{" "}
                <button
                  onClick={() => navigate(ROUTES.SETTINGS.GROUPS)}
                  className="font-semibold text-ink-primary hover:underline"
                >
                  Groups
                </button>{" "}
                page. Membership is resolved lazily — when a user logs in, Photon checks their GW group membership and grants access automatically.
              </li>
              <li className="text-xs text-ink-gray-500 dark:text-ink-gray-400 leading-relaxed">
                Memberships can also be refreshed on demand using the <strong className="text-ink-gray-700 dark:text-ink-gray-300">Sync</strong> button in each group's detail panel.
              </li>
            </ul>
            <div className="pt-1 border-t border-edge-gray-200 dark:border-edge-gray-700">
              <button
                onClick={() => navigate(ROUTES.SETTINGS.GROUPS)}
                className="text-xs font-semibold text-ink-primary hover:underline"
              >
                Go to Groups →
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showConfirmDisable}
        title="Disable Google Workspace Integration"
        confirmLabel="Disable"
        confirmVariant="danger"
        isLoading={deleting}
        onCancel={() => setShowConfirmDisable(false)}
        onConfirm={handleDisable}
      >
        This will remove the GW domain config. Existing Google Workspace groups will remain but will
        no longer receive login-time membership updates.
      </ConfirmDialog>
    </SettingsPageShell>
  );
};

export default GoogleWorkspaceConfigPage;
